export const LUT_SIZE = 33;

export const DEFAULT_GRADE = {
  exposure: 0, contrast: 0, saturation: 1, temperature: 0, tint: 0,
  shadows: 0, highlights: 0, depth: 0, density: 0, mix: 1,
  lift: { x: 0, y: 0, l: 0 }, gamma: { x: 0, y: 0, l: 0 }, gain: { x: 0, y: 0, l: 0 },
};

export const mergeGrade = (p = {}) => ({
  ...DEFAULT_GRADE, ...p,
  lift: { ...DEFAULT_GRADE.lift, ...(p.lift || {}) },
  gamma: { ...DEFAULT_GRADE.gamma, ...(p.gamma || {}) },
  gain: { ...DEFAULT_GRADE.gain, ...(p.gain || {}) },
});

const W = [0.2126, 0.7152, 0.0722];
const luma = (c) => c[0] * W[0] + c[1] * W[1] + c[2] * W[2];
const sstep = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
const clamp01 = (v) => Math.min(1, Math.max(0, v));

export const wheelRGB = ({ x = 0, y = 0 }) => {
  const a = Math.hypot(x, y);
  if (!a) return [0, 0, 0];
  const h = Math.atan2(y, x);
  return [Math.cos(h) * a, Math.cos(h - 2.0944) * a, Math.cos(h + 2.0944) * a];
};

export function applyGrade(r, g, b, p) {
  const orig = [r, g, b];
  const ex = Math.pow(2, p.exposure);
  let c = orig.map((v) => v * ex);
  c[0] *= 1 + 0.18 * p.temperature + 0.04 * p.tint;
  c[1] *= 1 - 0.12 * p.tint;
  c[2] *= 1 - 0.18 * p.temperature + 0.04 * p.tint;
  const L = wheelRGB(p.lift).map((v) => v * 0.2 + p.lift.l * 0.2);
  c = c.map((v, i) => v + L[i] * (1 - v));
  const G = wheelRGB(p.gain).map((v) => 1 + v * 0.4 + p.gain.l * 0.5);
  c = c.map((v, i) => v * G[i]);
  const Ga = wheelRGB(p.gamma).map((v) => 1 + v * 0.4 + p.gamma.l * 0.5);
  c = c.map((v, i) => Math.pow(Math.max(v, 0), 1 / Math.max(Ga[i], 0.05)));
  let Y = luma(c);
  const sw = 1 - sstep(0, 0.55, Y), hw = sstep(0.45, 1, Y);
  c = c.map((v) => v + p.shadows * 0.3 * sw + p.highlights * 0.3 * hw);
  c = c.map((v) => 0.435 + (v - 0.435) * (1 + p.contrast));
  c = c.map((v) => { const t = clamp01(v); return v + (t * t * (3 - 2 * t) - t) * p.depth * 0.9; });
  Y = clamp01(luma(c));
  c = c.map((v) => v * (1 - p.depth * 0.12 * (1 - Y) * (1 - Y)));
  Y = luma(c);
  c = c.map((v) => Y + (v - Y) * p.saturation);
  const S = Math.max(...c) - Math.min(...c);
  c = c.map((v) => v * (1 - p.density * 0.4 * clamp01(S)));
  return c.map((v, i) => clamp01(orig[i] * (1 - p.mix) + clamp01(v) * p.mix));
}

export function buildLut(p, N = LUT_SIZE) {
  const out = new Float32Array(N * N * N * 3);
  let i = 0;
  for (let b = 0; b < N; b++)
    for (let g = 0; g < N; g++)
      for (let r = 0; r < N; r++) {
        const c = applyGrade(r / (N - 1), g / (N - 1), b / (N - 1), p);
        out[i++] = c[0]; out[i++] = c[1]; out[i++] = c[2];
      }
  return out;
}

export function lutToTexture(lut, N = LUT_SIZE) {
  const data = new Uint8Array(N * N * N * 4);
  for (let b = 0; b < N; b++)
    for (let g = 0; g < N; g++)
      for (let r = 0; r < N; r++) {
        const s = ((b * N + g) * N + r) * 3, d = (g * N * N + b * N + r) * 4;
        data[d] = lut[s] * 255; data[d + 1] = lut[s + 1] * 255; data[d + 2] = lut[s + 2] * 255; data[d + 3] = 255;
      }
  return data;
}

export function toCube(p, title, N = LUT_SIZE) {
  const lut = buildLut(p, N);
  const lines = [
    `TITLE "${title.replace(/"/g, "")}"`,
    "# Created with Celestial Light Studio",
    "# Input: Rec.709 display-referred. Apply as a 3D LUT in Premiere, Resolve, Final Cut Pro, Avid or OBS.",
    `LUT_3D_SIZE ${N}`, "DOMAIN_MIN 0.0 0.0 0.0", "DOMAIN_MAX 1.0 1.0 1.0",
  ];
  for (let i = 0; i < lut.length; i += 3) lines.push(`${lut[i].toFixed(6)} ${lut[i + 1].toFixed(6)} ${lut[i + 2].toFixed(6)}`);
  return lines.join("\n") + "\n";
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

const hv = (deg, a, l = 0) => ({ x: +(Math.cos((deg * Math.PI) / 180) * a).toFixed(3), y: +(Math.sin((deg * Math.PI) / 180) * a).toFixed(3), l });

export const PRESETS = [
  { id: "blockbuster", name: "Blockbuster Teal & Orange", ref: "Modern action blockbusters", swatch: ["#0f4c5c", "#e36414"],
    params: { contrast: 0.3, saturation: 1.2, lift: hv(190, 0.28), gain: hv(30, 0.25), depth: 0.4 } },
  { id: "code-rain", name: "Code Rain", ref: "Inspired by The Matrix", swatch: ["#0b2a12", "#7bd389"],
    params: { temperature: -0.2, tint: -0.5, saturation: 0.8, contrast: 0.3, gamma: hv(120, 0.25), depth: 0.5 } },
  { id: "amber-haze", name: "Amber Haze", ref: "Inspired by Blade Runner 2049", swatch: ["#3a1f04", "#f4a259"],
    params: { temperature: 0.6, saturation: 0.9, contrast: 0.15, highlights: -0.2, gain: hv(30, 0.25), lift: hv(190, 0.1), density: 0.4 } },
  { id: "atomic-kodak", name: "Atomic Kodak", ref: "Inspired by Oppenheimer", swatch: ["#2b1d14", "#e9c46a"],
    params: { temperature: 0.25, contrast: 0.35, saturation: 0.85, depth: 0.6, density: 0.5, lift: hv(25, 0.1) } },
  { id: "neon-night", name: "Neon Night", ref: "Inspired by Euphoria", swatch: ["#2d0b3d", "#ff5fd2"],
    params: { tint: 0.35, saturation: 1.35, contrast: 0.25, lift: hv(280, 0.22), gain: hv(320, 0.12) } },
  { id: "desert-spice", name: "Desert Spice", ref: "Inspired by Dune", swatch: ["#4a2c12", "#e0a96d"],
    params: { temperature: 0.5, saturation: 0.75, contrast: 0.1, highlights: 0.1, gamma: hv(40, 0.18), density: 0.3 } },
  { id: "upside-80s", name: "Eighties Upside", ref: "Inspired by Stranger Things", swatch: ["#101a3d", "#e85d3f"],
    params: { temperature: -0.1, saturation: 1.15, contrast: 0.2, lift: hv(240, 0.2), gain: hv(25, 0.15) } },
  { id: "boardroom", name: "Boardroom Cool", ref: "Inspired by Succession", swatch: ["#1e2a33", "#b7c4cf"],
    params: { temperature: -0.15, saturation: 0.7, contrast: 0.1, depth: 0.3, density: 0.2 } },
  { id: "sodium-stairs", name: "Sodium Stairs", ref: "Inspired by Joker", swatch: ["#1f2b12", "#d4a017"],
    params: { temperature: 0.3, tint: -0.15, saturation: 1.1, contrast: 0.3, gamma: hv(80, 0.22), depth: 0.5 } },
  { id: "abq-heat", name: "Albuquerque Heat", ref: "Inspired by Breaking Bad", swatch: ["#4d3a05", "#f2c14e"],
    params: { temperature: 0.7, tint: -0.1, saturation: 1.1, contrast: 0.25, gain: hv(55, 0.2) } },
  { id: "frontier-cold", name: "Frontier Cold", ref: "Inspired by The Revenant", swatch: ["#1b2631", "#cfd8dc"],
    params: { temperature: -0.4, saturation: 0.6, contrast: 0.2, depth: 0.4, highlights: -0.1 } },
  { id: "montmartre", name: "Montmartre Dream", ref: "Inspired by Amélie", swatch: ["#21391f", "#e5b842"],
    params: { tint: -0.2, saturation: 1.3, gain: hv(50, 0.22), lift: hv(120, 0.15), density: 0.5 } },
  { id: "fury", name: "Fury Wasteland", ref: "Inspired by Mad Max: Fury Road", swatch: ["#0b3954", "#ff7f11"],
    params: { temperature: 0.6, saturation: 1.5, contrast: 0.45, gain: hv(30, 0.2), lift: hv(195, 0.2), depth: 0.4 } },
  { id: "miami-blue", name: "Miami Blue", ref: "Inspired by Moonlight", swatch: ["#073b4c", "#c86bfa"],
    params: { saturation: 1.25, contrast: 0.2, lift: hv(185, 0.22), gamma: hv(300, 0.12) } },
  { id: "gotham-rust", name: "Gotham Rust", ref: "Inspired by The Batman", swatch: ["#1a0f0b", "#a4161a"],
    params: { exposure: -0.3, temperature: 0.2, saturation: 0.7, contrast: 0.4, depth: 0.8, density: 0.6, lift: hv(10, 0.12) } },
];
